import { useCallback, useEffect } from 'react';
import {
  flightRoomsQuery,
  FlightRoomsQueryOptions,
  getFlightRoomById,
  getFlightRooms,
  getResolvedRoomIdByExternalId,
  searchFlightRooms,
} from '@connect-x-app/hybrid-data-access';
import {
  ApiError,
  FlightRoomSearchSummary,
  FlightRoomSummary,
  FlightStatus,
  HttpStatus,
  Pagination,
  QueryKeys,
} from '@connect-x-app/shared-domain-models';
import { DEFAULT_PAGE_SIZE, SortAscending, TYPEAHEAD_DEBOUNCE_TIME, useDebounce } from '@connect-x-app/shared-utils';
import { InfiniteData, QueryKey, skipToken, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { useFlightRooms } from '../feature';

export const useSearchPaginatedFlightRooms = (search: string) => {
  // A typed search waits until the typing stops; the empty one the mobile add screen opens with goes out at once.
  const debouncedSearch = useDebounce<string | null>(search, TYPEAHEAD_DEBOUNCE_TIME, search === '' ? '' : null);

  return useInfiniteQuery<
    Pagination<FlightRoomSearchSummary>,
    ApiError,
    InfiniteData<Pagination<FlightRoomSummary>>,
    QueryKey,
    number
  >({
    gcTime: 0,
    getNextPageParam: ({ items, limit, offset }) => {
      if (items?.length !== limit) {
        return undefined;
      }

      return offset + limit;
    },
    initialPageParam: 0,
    queryFn:
      debouncedSearch === null
        ? skipToken
        : ({ pageParam }) =>
            searchFlightRooms({
              limit: DEFAULT_PAGE_SIZE,
              offset: pageParam,
              query: debouncedSearch,
            }),
    queryKey: [QueryKeys.SEARCH_FLIGHTROOM, debouncedSearch],
    select: data => ({
      ...data,
      pages: data.pages.map(page => ({
        ...page,
        items: page.items?.map(item => ({
          ...item,
          accessRequestCount: 0,
          hasUnreadMentions: false,
          unreadMessagesCount: 0,
        })),
      })),
    }),
    staleTime: 0,
  });
};

export const useGetPaginatedFlightRooms = (status?: FlightStatus.ACTIVE | FlightStatus.LANDED, workstationId?: string) => {
  const { resetFlightRooms, setFlightRooms } = useFlightRooms();

  const updateFlightRooms = useCallback(
    (pages: Pagination<FlightRoomSummary>[]) => {
      const flightRooms = pages.reduce((accu: FlightRoomSummary[], page) => [...accu, ...(page.items ?? [])], []);
      setFlightRooms(flightRooms, status!);
    },
    [setFlightRooms, status],
  );

  const queryResult = useInfiniteQuery<
    Pagination<FlightRoomSummary>,
    ApiError,
    InfiniteData<Pagination<FlightRoomSummary>>,
    QueryKey,
    number
  >({
    enabled: !!status,
    gcTime: 0,
    getNextPageParam: ({ items, limit, offset }) => {
      if (items?.length !== limit) {
        return undefined;
      }

      return offset + limit;
    },
    initialPageParam: 0,
    queryFn: ({ pageParam }) => {
      const query: FlightRoomsQueryOptions = {
        ...flightRoomsQuery,
        offset: pageParam,
        sort: [new SortAscending<FlightRoomSummary>('schedule.std')],
        status: status!,
        workstationId: workstationId,
      };

      return getFlightRooms(query);
    },
    queryKey: [QueryKeys.FLIGHTROOMS, status, workstationId],
    retry: 0,
    staleTime: 0,
  });

  useEffect(() => {
    if (queryResult.data) {
      const pages = queryResult.data.pages;
      updateFlightRooms(pages);
    }
  }, [queryResult.data, updateFlightRooms]);

  useEffect(() => {
    if (queryResult.error?.statusCode === HttpStatus.FORBIDDEN) {
      resetFlightRooms();
    }
  }, [queryResult.error?.statusCode, resetFlightRooms]);

  return queryResult;
};

export const useGetFlightRoomById = (roomId: string | null) =>
  useQuery({
    enabled: !!roomId,
    gcTime: 0,
    queryFn: () => getFlightRoomById(roomId!),
    queryKey: [QueryKeys.FLIGHTROOM, roomId],
    retry: false,
    staleTime: 0,
  });

export const useResolveExternalRoom = (externalRoomId: string | undefined) => {
  return useQuery<{ id: string }, ApiError>({
    enabled: !!externalRoomId,
    gcTime: 0,
    queryFn: () => getResolvedRoomIdByExternalId(externalRoomId!),
    queryKey: [QueryKeys.RESOLVE_EXTERNAL_ROOM, externalRoomId],
    retry: false,
    staleTime: 0,
  });
};
